/**
 * **영토전**(Todo9)의 땅과 세계 상태. 1인용 시제품이다 — 서버 없이 계정에 적는다.
 *
 * 출정은 깊이를 깨러 들어가는 것이고, 영토전은 옛 삼국의 땅을 하나씩 빼앗는 것이다. 나라마다 여덟 곳, 그중 하나가 수도다.
 * **수도는 점령할 수 없다** — 한 나라가 지워져 판이 끝나지 않게. 맞닿은 땅만 칠 수 있다(전선).
 *
 * 지도는 실제 지형을 본뜬 그림이 아니라 자리표다(`x`·`y`, 지도 상자 0–100 × 0–160). 지명은 삼국시대 것이고, 경계는 5–6세기
 * 어림이다(지명은 그 시기의 것을 중심으로 하되 무진주처럼 널리 알려진 후대 이름을 쓴 곳도 있다 — 무진주는 백제 때 노지).
 * 땅마다 싸울 지형(전장 특성의 땅)과 지키는 병력의 세기(깊이로 적는다, `garrisonDepth`)가 정해져 있다.
 *
 * 시뮬레이션 밖이다(전투는 `depthRoster`·`createBattle`을 그대로 쓴다). 그래도 서버가 나중에 같은 계산을 하도록 정수만 쓴다.
 */
import { Culture, TROOP_BY_ID } from '../sim/troops.ts';
import type { Soldier } from './army.ts';
import type { SpecialtyKind } from './specialty.ts';
import type { SeasonRecord } from './season.ts';
import { Trait } from '../sim/traits.ts';

export interface TerritoryDef {
  readonly id: string;
  readonly name: string;
  readonly hanja: string;
  /** 처음 주인(옛 나라). */
  readonly culture: Culture;
  readonly capital?: boolean;
  /** 지도 자리(0–100 × 0–160). */
  readonly x: number;
  readonly y: number;
  /** 그 땅에서 싸울 때의 지형(전장 특성의 땅). */
  readonly land: Trait;
  /**
   * 이름을 점의 어느 쪽에 적나(없으면 위). 남쪽이 빽빽해 모두 위에 적었더니 삼년산성·웅진성, 사비성·금마저가 겹쳤다.
   */
  readonly label?: 'n' | 's' | 'e' | 'w';
  /**
   * 그 땅의 특산(`meta/specialty.ts`). 지명의 옛 이미지에서 골랐다 — 부여·책성·탐라·하슬라는 말, 국내성·한성·삼년산성·금관은
   * 철 무기(가야의 철), 금성·사비·웅진은 금 공예(투구·관) 같은 식.
   */
  readonly specialty: SpecialtyKind;
}

/** 지도 상자의 크기(자리표의 단위). */
export const MAP_W = 100;
export const MAP_H = 160;

const G = Culture.Goguryeo;
const B = Culture.Baekje;
const S = Culture.Silla;

export const TERRITORIES: readonly TerritoryDef[] = [
  // 고구려 — 요동·만주와 대동강
  { id: 'pyeongyang', name: '평양성', hanja: '平壤城', culture: G, capital: true, x: 40, y: 64, land: Trait.HighGround, specialty: 'armor' },
  { id: 'gungnae', name: '국내성', hanja: '國內城', culture: G, x: 52, y: 34, land: Trait.MountainPath, specialty: 'weapon' },
  { id: 'yodong', name: '요동성', hanja: '遼東城', culture: G, x: 26, y: 24, land: Trait.Plains, specialty: 'armor' },
  { id: 'ansi', name: '안시성', hanja: '安市城', culture: G, x: 20, y: 36, land: Trait.HighGround, specialty: 'shield' },
  { id: 'bisa', name: '비사성', hanja: '卑沙城', culture: G, x: 12, y: 50, land: Trait.Ford, label: 'w', specialty: 'banner' },
  { id: 'buyeo', name: '부여성', hanja: '扶餘城', culture: G, x: 56, y: 10, land: Trait.Plains, specialty: 'horse' },
  { id: 'chaek', name: '책성', hanja: '柵城', culture: G, x: 82, y: 20, land: Trait.Forest, specialty: 'horse' },
  { id: 'nampyeong', name: '남평양', hanja: '南平壤', culture: G, x: 38, y: 80, land: Trait.Marsh, specialty: 'banner' },
  // 백제 — 한강에서 남서 해안
  { id: 'sabi', name: '사비성', hanja: '泗沘城', culture: B, capital: true, x: 38, y: 112, land: Trait.Ford, label: 'w', specialty: 'helm' },
  { id: 'hanseong', name: '한성', hanja: '漢城', culture: B, x: 46, y: 90, land: Trait.Ford, specialty: 'weapon' },
  { id: 'ungjin', name: '웅진성', hanja: '熊津城', culture: B, x: 48, y: 104, land: Trait.HighGround, label: 's', specialty: 'helm' },
  { id: 'garim', name: '가림성', hanja: '加林城', culture: B, x: 32, y: 100, land: Trait.Forest, label: 'w', specialty: 'shield' },
  { id: 'geumma', name: '금마저', hanja: '金馬渚', culture: B, x: 42, y: 122, land: Trait.Plains, label: 'e', specialty: 'banner' },
  { id: 'gosabu', name: '고사부리', hanja: '古沙夫里', culture: B, x: 36, y: 130, land: Trait.Marsh, label: 'w', specialty: 'helm' },
  { id: 'mujin', name: '무진주', hanja: '武珍州', culture: B, x: 40, y: 140, land: Trait.Forest, label: 'w', specialty: 'armor' },
  { id: 'tamna', name: '탐라', hanja: '耽羅', culture: B, x: 34, y: 155, land: Trait.Plains, specialty: 'horse' },
  // 신라 — 동해안과 낙동강
  { id: 'geumseong', name: '금성', hanja: '金城', culture: S, capital: true, x: 77, y: 122, land: Trait.Plains, label: 'e', specialty: 'helm' },
  { id: 'haseulla', name: '하슬라', hanja: '何瑟羅', culture: S, x: 66, y: 84, land: Trait.MountainPath, specialty: 'horse' },
  { id: 'siljik', name: '실직', hanja: '悉直', culture: S, x: 74, y: 100, land: Trait.Canyon, label: 'e', specialty: 'shield' },
  { id: 'sabeol', name: '사벌주', hanja: '沙伐州', culture: S, x: 62, y: 108, land: Trait.Plains, label: 'e', specialty: 'armor' },
  { id: 'samnyeon', name: '삼년산성', hanja: '三年山城', culture: S, x: 56, y: 98, land: Trait.HighGround, specialty: 'weapon' },
  { id: 'dalgubeol', name: '달구벌', hanja: '達句伐', culture: S, x: 68, y: 120, land: Trait.Marsh, specialty: 'banner' },
  { id: 'daeya', name: '대야성', hanja: '大耶城', culture: S, x: 58, y: 126, land: Trait.Canyon, label: 's', specialty: 'armor' },
  { id: 'geumgwan', name: '금관', hanja: '金官', culture: S, x: 66, y: 138, land: Trait.Ford, label: 's', specialty: 'weapon' },
];

/** 맞닿은 땅(한 번씩만 적는다 — 양쪽으로 쓴다). 나라 사이의 선이 전선이다. */
const LINKS: readonly (readonly [string, string])[] = [
  // 고구려 안
  ['buyeo', 'yodong'], ['buyeo', 'gungnae'], ['buyeo', 'chaek'],
  ['yodong', 'ansi'], ['yodong', 'gungnae'], ['ansi', 'bisa'], ['ansi', 'pyeongyang'],
  ['gungnae', 'pyeongyang'], ['gungnae', 'chaek'], ['bisa', 'pyeongyang'], ['pyeongyang', 'nampyeong'],
  // 백제 안
  ['hanseong', 'ungjin'], ['hanseong', 'garim'], ['ungjin', 'sabi'], ['garim', 'sabi'],
  ['sabi', 'geumma'], ['ungjin', 'geumma'], ['geumma', 'gosabu'], ['gosabu', 'mujin'], ['mujin', 'tamna'],
  // 신라 안
  ['haseulla', 'siljik'], ['haseulla', 'sabeol'], ['siljik', 'geumseong'], ['sabeol', 'samnyeon'],
  ['sabeol', 'dalgubeol'], ['dalgubeol', 'geumseong'], ['dalgubeol', 'daeya'], ['daeya', 'geumgwan'],
  ['geumgwan', 'geumseong'],
  // 전선
  ['nampyeong', 'hanseong'], ['nampyeong', 'haseulla'], ['chaek', 'haseulla'],
  ['hanseong', 'samnyeon'], ['ungjin', 'samnyeon'], ['geumma', 'daeya'], ['mujin', 'geumgwan'],
];

export const TERRITORY_BY_ID: ReadonlyMap<string, TerritoryDef> = new Map(TERRITORIES.map((t) => [t.id, t]));

/** 맞닿은 땅의 id. `TERRITORIES` 순서로 — 화면과 계산이 늘 같은 순서로 돈다. */
const NEIGHBORS: ReadonlyMap<string, readonly string[]> = (() => {
  const m = new Map<string, Set<string>>(TERRITORIES.map((t) => [t.id, new Set<string>()]));
  for (const [a, b] of LINKS) {
    m.get(a)!.add(b);
    m.get(b)!.add(a);
  }
  const order = new Map(TERRITORIES.map((t, i) => [t.id, i]));
  return new Map([...m].map(([id, s]) => [id, [...s].sort((p, q) => order.get(p)! - order.get(q)!)]));
})();

export function neighborsOf(id: string): readonly string[] {
  return NEIGHBORS.get(id) ?? [];
}

/** 지도의 선(양쪽 한 번씩). 화면이 그린다. */
export function territoryLinks(): readonly (readonly [string, string])[] {
  return LINKS;
}

/**
 * 지키는 병력의 세기를 깊이로(`depthRoster`). 수도에 가까울수록 세다 — 수도에서 같은 나라 땅을 몇 걸음 건너는가(옛 경계 안).
 * 한 걸음 18, 두 걸음 13, 세 걸음 9, 그 너머 7. 변두리부터 뺏고, 깊이 들어갈수록 출정에서 깊이를 깬 부대가 필요하다.
 * 수도는 치지 못하므로 쓰지 않는다(그래도 값은 둔다 — 23).
 */
const STEP_DEPTH = [23, 18, 13, 9, 7] as const;

const GARRISON_DEPTH: ReadonlyMap<string, number> = (() => {
  const out = new Map<string, number>();
  for (const cap of TERRITORIES.filter((t) => t.capital)) {
    const dist = new Map<string, number>([[cap.id, 0]]);
    const queue = [cap.id];
    while (queue.length > 0) {
      const at = queue.shift()!;
      for (const n of neighborsOf(at)) {
        if (dist.has(n) || TERRITORY_BY_ID.get(n)!.culture !== cap.culture) continue;
        dist.set(n, dist.get(at)! + 1);
        queue.push(n);
      }
    }
    for (const [id, d] of dist) out.set(id, STEP_DEPTH[Math.min(d, STEP_DEPTH.length - 1)]);
  }
  return out;
})();

export function garrisonDepth(id: string): number {
  return GARRISON_DEPTH.get(id) ?? STEP_DEPTH[STEP_DEPTH.length - 1];
}

/** 한 땅의 내 수비대(`meta/garrison.ts`). */
export interface Garrison {
  /** 둔 병사 — 계정의 병사 목록(`Army.soldiers`)에서 옮겨 온 그대로. 거두면 돌아간다. */
  soldiers: Soldier[];
  /** 수비 대장(동료 id). 없으면 null. */
  captain: string | null;
  /**
   * 이 수비대가 최소 투입(`STAKE_MIN`명)을 채운 차례. 한 차례를 온전히 넘긴 뒤부터 특산 점수를 쌓는다(`specialtyRate`). 처음에는
   * 수비대를 만든 차례를 적어, 대장만 세워 둔 수비대에 한참 뒤 병사를 채우면 기다림 없이 쌓였다(리뷰가 짚었다). 최소 아래로
   * 내려가면 지운다(`specialtyPhase`) — 다시 채운 차례부터 센다.
   */
  since?: number;
}

/** 세계 상태. 계정(`Army.realm`)에 적는다. */
export interface RealmState {
  /** 지난 차례 수. 출정 한 번이나 영토 전투 한 판이 끝날 때 돈다(`realmTick`). */
  turn: number;
  /** 땅 → 지금 주인. 모든 땅이 적혀 있다(`normalizeRealm`). */
  owner: Record<string, Culture>;
  /** 내 수비대(땅 id → 수비대). 없는 땅은 적지 않는다. */
  garrisons?: Record<string, Garrison>;
  /** 지난 차례에 수비대가 낸 군자금(지도 화면이 알린다). */
  lastIncome?: { turn: number; gold: number };
  /**
   * 다친 수비 병사(6단계, `meta/campaign.ts`). 병영에도 수비대에도 없다 — `until` 차례가 되면 병영으로 돌아온다. 둔 병사처럼
   * 계정 목록 밖에 두어 출정·편성이 저절로 보지 않는다.
   */
  infirmary?: Infirmed[];
  /** 땅 → 마지막으로 주인이 바뀐 차례. 막 바뀐 땅은 `GRACE_TURNS` 동안 되찾으러 오지 않는다(핑퐁 막기, Todo9 함정 6). */
  heldSince?: Record<string, number>;
  /** 영토 소식(각 나라 장수의 공격과 그 결말, 받은 특산). 새것이 뒤, `LOG_KEEP`개까지. */
  log?: RealmEvent[];
  /**
   * 지도를 마지막으로 열어 본 차례(`markNewsSeen`). 이 뒤에 난 우리 나라의 일이 있으면 출정 화면의 영토 단추에 빨간 점을
   * 단다(`hasUnseenNews`). 계정에 적는다 — 화면 세션에만 두면 새로 고칠 때마다 본 소식에 점이 다시 붙는다.
   */
  newsSeen?: number;
  /** 특산 점수 장부(땅 id → 점수). 사람의 장부라 땅을 잃어도 남는다(`meta/specialty.ts`). */
  ledger?: Record<string, number>;
  /** 지금 시즌 번호(1부터, 없으면 1, `meta/season.ts`). */
  season?: number;
  /** 이번 시즌이 시작한 차례(차례 번호는 되돌리지 않는다). */
  seasonStart?: number;
  /** 이번 시즌의 누적 보유 점수. */
  seasonScore?: number;
  /** 끝난 시즌의 기록. */
  history?: SeasonRecord[];
}

export interface Infirmed {
  soldier: Soldier;
  /** 이 차례가 되면 돌아온다. */
  until: number;
}

/** 영토의 일 한 건. 글은 화면이 짓는다(조사 등은 UI의 일). */
export interface RealmEvent {
  turn: number;
  /**
   * 'held' 우리가 막아 냈다, 'lost' 우리가 빼앗겼다, 'gift' 특산을 받았다(`item`), 'war' 나라끼리 — `by`가 `victim`의 땅을
   * 빼앗았다, 'repel' 나라끼리 — `victim`이 막아 냈다.
   */
  kind: 'held' | 'lost' | 'gift' | 'war' | 'repel';
  /** 나라끼리의 싸움에서 친 쪽이 아닌 나라('war'·'repel'). */
  victim?: Culture;
  /** 받은 특산(장비 id, 'gift'만). */
  item?: string;
  territory: string;
  /** 친 나라. */
  by: Culture;
  /** 친 장수(그 나라 동료 id — 옛 이름난 장수). */
  general: string;
  /** 우리 수비대: 전사·부상 수, 막아 냈을 때 받은 군자금. */
  dead: number;
  hurt: number;
  bounty: number;
}

/**
 * 우리 나라의 일인가: 우리 땅을 막아 냈거나 빼앗겼거나, 특산을 받았다. 나라끼리의 싸움('war'·'repel')은 우리 나라가 끼지
 * 않는다 — 우리 땅이 맞으면 수비대가 없어도 'held'·'lost'로 적힌다(`resolveAttack`).
 */
export function isOurNews(e: RealmEvent): boolean {
  return e.kind === 'held' || e.kind === 'lost' || e.kind === 'gift';
}

/** 지도를 열어 본 뒤에 난 우리 나라의 일이 있는가(영토 단추의 빨간 점). */
export function hasUnseenNews(realm: RealmState | undefined): boolean {
  if (!realm?.log) return false;
  const seen = realm.newsSeen ?? 0;
  return realm.log.some((e) => e.turn > seen && isOurNews(e));
}

/** 지도를 열어 봤다. 바뀌었으면 true(저장할 일이 생겼다). */
export function markNewsSeen(realm: RealmState): boolean {
  if (realm.newsSeen === realm.turn) return false;
  realm.newsSeen = realm.turn;
  return true;
}

/** 소식은 이만큼만 남긴다. 나라끼리의 싸움이 들며 차례마다 소식이 늘어 12에서 늘렸다. */
export const LOG_KEEP = 20;

/**
 * 장부에 남을 수 있는 가장 큰 점수 = 특산 하나의 값(`SPECIALTY_GOAL` 100) − 1. 정상 장부는 지급 뒤 늘 0–99다. 저장의 큰 값을
 * 그대로 받았더니 한 차례에 값 ÷ 100개를 지급해(2^53이면 90조 번) 화면이 멈췄다(리뷰가 짚었다). `specialty.ts`의 시험이
 * 두 값이 맞는지 본다(여기서 그 상수를 가져오면 모듈이 서로 물린다).
 */
export const LEDGER_MAX = 99;
/** 수비대가 특산 점수를 쌓으려면 둔 병사가 이만큼 있어야 한다(`specialty.ts`의 `MIN_STAKE`가 이 값을 쓴다). */
export const STAKE_MIN = 4;

/** 계정 목록 밖에 있는 계정의 사람: 수비대 + 다친 병사. 이름·사람 수를 셀 때 쓴다. */
export function realmSoldiers(realm: RealmState | undefined): Soldier[] {
  return [...garrisonedSoldiers(realm), ...(realm?.infirmary ?? []).map((x) => x.soldier)];
}

/** 수비 중인 병사 전부(땅 순서). 이름이 겹치지 않게 볼 때 쓴다(`takenNames`). */
export function garrisonedSoldiers(realm: RealmState | undefined): Soldier[] {
  if (!realm?.garrisons) return [];
  const out: Soldier[] = [];
  for (const t of TERRITORIES) out.push(...(realm.garrisons[t.id]?.soldiers ?? []));
  return out;
}

function isSoldier(v: unknown): v is Soldier {
  if (!v || typeof v !== 'object') return false;
  const s = v as Soldier;
  return Number.isSafeInteger(s.uid) && s.uid > 0 && typeof s.troop === 'string' && TROOP_BY_ID.has(s.troop) && Number.isFinite(s.xp) && s.xp >= 0;
}

export function createRealm(): RealmState {
  const owner: Record<string, Culture> = {};
  for (const t of TERRITORIES) owner[t.id] = t.culture;
  // 시즌 칸을 처음부터 적는다. 비워 두었더니 불러올 때 시즌 이전의 저장으로 읽혀 첫 시즌이 그 차례부터 다시 시작했다 —
  // 진행은 0으로, 쌓인 점수는 남은 채(리뷰·QA가 짚었다).
  return { turn: 0, owner, season: 1, seasonStart: 0, seasonScore: 0 };
}

function isCulture(v: unknown): v is Culture {
  return v === Culture.Goguryeo || v === Culture.Baekje || v === Culture.Silla;
}

/**
 * 저장에서 읽은 세계 상태를 맞춘다. 없으면 처음 지도, 모르는 땅은 털고, 빠진 땅은 처음 주인으로, 수도는 늘 제 나라.
 * 손으로 고친 저장이나 땅 목록이 바뀐 뒤의 저장도 열린다.
 */
export function normalizeRealm(raw: unknown): RealmState {
  const fresh = createRealm();
  if (!raw || typeof raw !== 'object') return fresh;
  const r = raw as Partial<RealmState>;
  // 안전한 정수만(리뷰 제안) — 2^53을 넘으면 1을 더해도 차례가 늘지 않는다.
  const turn = Number.isSafeInteger(r.turn) && (r.turn as number) >= 0 ? (r.turn as number) : 0;
  const owner: Record<string, Culture> = {};
  for (const t of TERRITORIES) {
    const v = r.owner && typeof r.owner === 'object' ? (r.owner as Record<string, unknown>)[t.id] : undefined;
    owner[t.id] = t.capital ? t.culture : isCulture(v) ? v : t.culture;
  }
  // 수비대: 있는 땅·수도 아닌 땅만, 병사는 모양이 맞는 것만(병종이 사라진 병사는 털린다 — 계정 목록과 같은 규칙). 땅의
  // 주인이 계정의 나라인지, uid가 계정 목록과 겹치지 않는지는 계정을 알아야 해서 `reconcileGarrisons`가 본다.
  const out: RealmState = { turn, owner };
  const rawG = r.garrisons;
  if (rawG && typeof rawG === 'object') {
    const garrisons: Record<string, Garrison> = {};
    for (const t of TERRITORIES) {
      const g = (rawG as Record<string, unknown>)[t.id] as Partial<Garrison> | undefined;
      if (t.capital || !g || typeof g !== 'object') continue;
      const soldiers = Array.isArray(g.soldiers) ? g.soldiers.filter(isSoldier) : [];
      const captain = typeof g.captain === 'string' ? g.captain : null;
      const since = Number.isSafeInteger(g.since) && (g.since as number) >= 0 ? (g.since as number) : undefined;
      if (soldiers.length > 0 || captain) garrisons[t.id] = { soldiers, captain, ...(since !== undefined ? { since } : {}) };
    }
    if (Object.keys(garrisons).length > 0) out.garrisons = garrisons;
  }
  // 본 차례는 지난 차례를 넘지 못한다 — 앞선 값이면 앞으로 날 소식에 점이 붙지 않는다.
  if (Number.isSafeInteger(r.newsSeen) && (r.newsSeen as number) >= 0) out.newsSeen = Math.min(r.newsSeen as number, turn);
  const li = r.lastIncome;
  if (li && Number.isSafeInteger(li.turn) && Number.isSafeInteger(li.gold) && li.gold >= 0) out.lastIncome = { turn: li.turn, gold: li.gold };
  // 다친 병사: 모양이 맞는 것만. uid 겹침은 `reconcileGarrisons`가 본다.
  if (Array.isArray(r.infirmary)) {
    const inf = r.infirmary
      .filter((x): x is Infirmed => !!x && typeof x === 'object' && isSoldier((x as Infirmed).soldier) && Number.isSafeInteger((x as Infirmed).until))
      .map((x) => ({ soldier: x.soldier, until: x.until }));
    if (inf.length > 0) out.infirmary = inf;
  }
  if (r.heldSince && typeof r.heldSince === 'object') {
    const held: Record<string, number> = {};
    for (const t of TERRITORIES) {
      const v = (r.heldSince as Record<string, unknown>)[t.id];
      if (Number.isSafeInteger(v) && (v as number) >= 0) held[t.id] = v as number;
    }
    if (Object.keys(held).length > 0) out.heldSince = held;
  }
  // 시즌(`meta/season.ts`): 안전한 정수만. 시작 차례는 지금 차례보다 클 수 없다.
  if (Number.isSafeInteger(r.season) && (r.season as number) >= 1) out.season = r.season as number;
  // 시즌이 생기기 전의 저장(시즌 번호가 없다)은 지금 차례부터 첫 시즌을 센다 — 0부터 세면 60차례를 넘긴 계정이 다음 차례에
  // 점수 없이 곧바로 매겨졌다. 시즌 번호가 있는데 시작이 깨졌으면 0(그 시즌의 처음)으로.
  const legacy = r.season === undefined;
  out.seasonStart = Number.isSafeInteger(r.seasonStart) && (r.seasonStart as number) >= 0 ? Math.min(r.seasonStart as number, turn) : legacy ? turn : 0;
  if (legacy) {
    out.season = 1;
    out.seasonScore = 0;
  }
  // 옛 저장은 점수도 0부터 — 시작만 지금 차례로 옮기고 점수를 남기면 시즌이 늘어난 만큼 점수가 겹쳐 쌓였다(재리뷰가 짚었다).
  if (!legacy && Number.isSafeInteger(r.seasonScore) && (r.seasonScore as number) >= 0) out.seasonScore = r.seasonScore as number;
  if (Array.isArray(r.history)) {
    const grades = ['gold', 'silver', 'bronze', 'none'];
    const hist = r.history.filter(
      (h): h is SeasonRecord =>
        !!h &&
        typeof h === 'object' &&
        Number.isSafeInteger(h.season) &&
        Number.isSafeInteger(h.score) &&
        Number.isSafeInteger(h.par) &&
        Number.isSafeInteger(h.gold) &&
        grades.includes(h.grade) &&
        (h.item === null || typeof h.item === 'string'),
    );
    if (hist.length > 0) out.history = hist.map((h) => ({ season: h.season, score: h.score, par: h.par, grade: h.grade, gold: h.gold, item: h.item }));
  }
  if (r.ledger && typeof r.ledger === 'object') {
    const ledger: Record<string, number> = {};
    for (const t of TERRITORIES) {
      const v = (r.ledger as Record<string, unknown>)[t.id];
      if (Number.isSafeInteger(v) && (v as number) > 0) ledger[t.id] = Math.min(v as number, LEDGER_MAX);
    }
    if (Object.keys(ledger).length > 0) out.ledger = ledger;
  }
  if (Array.isArray(r.log)) {
    const log = r.log
      .filter(
        (e): e is RealmEvent =>
          !!e &&
          typeof e === 'object' &&
          Number.isSafeInteger(e.turn) &&
          (e.kind === 'held' ||
            e.kind === 'lost' ||
            (e.kind === 'gift' && typeof e.item === 'string') ||
            ((e.kind === 'war' || e.kind === 'repel') && isCulture(e.victim))) &&
          TERRITORY_BY_ID.has(e.territory) &&
          isCulture(e.by) &&
          typeof e.general === 'string' &&
          Number.isSafeInteger(e.dead) &&
          Number.isSafeInteger(e.hurt) &&
          Number.isSafeInteger(e.bounty),
      )
      .slice(-LOG_KEEP);
    if (log.length > 0) out.log = log;
  }
  return out;
}

/** 그 나라가 가진 땅(`TERRITORIES` 순서). */
export function territoriesOf(realm: RealmState, culture: Culture): TerritoryDef[] {
  return TERRITORIES.filter((t) => realm.owner[t.id] === culture);
}

/**
 * 그 나라가 칠 수 있는 땅: 제 땅과 맞닿은 남의 땅, 수도는 빼고. 영토 공격(다음 단계)과 지도의 전선 표시가 쓴다.
 */
export function attackableBy(realm: RealmState, culture: Culture): TerritoryDef[] {
  return TERRITORIES.filter(
    (t) => !t.capital && realm.owner[t.id] !== culture && neighborsOf(t.id).some((n) => realm.owner[n] === culture),
  );
}
